import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EQUIPMENT_TYPES } from "@/lib/equipmentTypes";
import { Switch } from "@/components/ui/switch";
import { CheckCircle, AlertCircle, Building2, Shield, MapPin, Calendar, Package, Eye, Layers, Weight, Ruler, AlertTriangle, Clock } from "lucide-react";
import { formatRelativeTime } from "@/lib/utils";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Truck, User } from "@shared/schema";
import TruckDetailModal from "@/components/TruckDetailModal";
import MemberLink from "@/components/MemberLink";

const MAPBOX_PUBLIC_TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN;

async function geocodeOriginClient(address: string): Promise<{ lat: number; lng: number } | null> {
  try {
    if (!MAPBOX_PUBLIC_TOKEN || !address.trim()) return null;
    const encoded = encodeURIComponent(address.trim());
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${MAPBOX_PUBLIC_TOKEN}&country=US&limit=1`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const feature = data?.features?.[0];
    if (!feature) return null;
    const [lng, lat] = feature.center;
    return { lat, lng };
  } catch {
    return null;
  }
}

interface PublicUserInfo {
  id: string;
  company: string;
  companyLogo?: string | null;
  dotNumber?: string;
  mcNumber?: string;
  adminVerified: boolean;
  upvoteCount: number;
  completedShipments: number;
  userType: string;
  lastActiveAt?: string | null;
  lastCapacityUpdate?: string | null;
}

interface TruckWithCarrier extends Truck {
  carrier: PublicUserInfo | null;
}

function formatLocation(city?: string | null, state?: string | null, zip?: string | null, fallback?: string) {
  if (city && state) {
    return zip ? `${city}, ${state} ${zip}` : `${city}, ${state}`;
  }
  return fallback || "Unknown Location";
}

function TruckCard({ truck, onViewDetails, currentUserVerified }: { truck: TruckWithCarrier; onViewDetails: (truck: TruckWithCarrier) => void; currentUserVerified: boolean }) {
  const formatDate = (dateString: Date) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const carrierInfo = truck.carrier;
  
  const originDisplay = formatLocation(truck.originCity, truck.originState, truck.originZip, truck.origin);
  const destinationDisplay = formatLocation(truck.destinationCity, truck.destinationState, truck.destinationZip, truck.destination);

  return (
    <Card 
      className="p-6 hover-elevate transition-all cursor-pointer" 
      data-testid={`card-truck-${truck.id}`}
      onClick={() => onViewDetails(truck)}
    >
      <div className="space-y-4">
        {carrierInfo && (
          <div className="flex items-center justify-between border-b pb-3">
            <div className="flex items-center gap-2 flex-wrap">
              {currentUserVerified ? (
                <MemberLink 
                  userId={carrierInfo.id} 
                  name={carrierInfo.company || "Unknown Carrier"}
                  companyLogo={carrierInfo.companyLogo}
                  showAvatar
                  avatarSize="sm"
                  className="font-medium text-sm"
                  data-testid="link-carrier-company"
                />
              ) : (
                <span className="text-sm font-medium text-muted-foreground" data-testid="text-carrier-hidden">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Carrier
                </span>
              )}
            </div>
            {currentUserVerified && carrierInfo.adminVerified && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Platform Verified
                </Badge>
              </div>
            )}
          </div>
        )}

        {currentUserVerified && carrierInfo?.dotNumber && (
          <div className="flex gap-4 text-xs text-muted-foreground flex-wrap">
            {carrierInfo.dotNumber && (
              <span data-testid="text-dot">DOT: {carrierInfo.dotNumber}</span>
            )}
            {carrierInfo.mcNumber && (
              <span data-testid="text-mc">MC: {carrierInfo.mcNumber}</span>
            )}
            {carrierInfo.completedShipments > 0 && (
              <span data-testid="text-shipments">{carrierInfo.completedShipments} completed</span>
            )}
          </div>
        )}
        {currentUserVerified && carrierInfo && (
          <div className="flex gap-3 text-xs text-muted-foreground flex-wrap">
            {formatRelativeTime(carrierInfo.lastActiveAt) && (
              <span className="flex items-center gap-1" data-testid="text-last-active">
                <Clock className="w-3 h-3" />
                Active {formatRelativeTime(carrierInfo.lastActiveAt)}
              </span>
            )}
            {formatRelativeTime(truck.lastConfirmedAt) && (
              <span className="flex items-center gap-1" data-testid="text-last-confirmed">
                <CheckCircle className="w-3 h-3" />
                Confirmed {formatRelativeTime(truck.lastConfirmedAt)}
              </span>
            )}
          </div>
        )}

        <div>
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-primary" />
            <h3 className="font-semibold text-lg" data-testid="text-route">
              {originDisplay} → {destinationDisplay}
            </h3>
          </div>
          <div className="space-y-2 text-base md:text-sm text-muted-foreground">
            {truck.equipmentType && (
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                <span className="font-medium" data-testid="text-equipment-type">{truck.equipmentType}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              <span data-testid="text-capacity">
                <span className="font-medium">Capacity:</span> {truck.capacity.toLocaleString()} lbs
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {truck.availablePallets != null && (
                <Badge variant="secondary" data-testid="badge-pallets">
                  <Layers className="w-3 h-3 mr-1" />
                  {truck.availablePallets} pallet spots
                </Badge>
              )}
              {truck.availableWeight != null && (
                <Badge variant="secondary" data-testid="badge-available-weight">
                  {truck.availableWeight.toLocaleString()} lbs avail.
                </Badge>
              )}
              {truck.availableLinearFeet != null && (
                <Badge variant="secondary" data-testid="badge-linear-feet">
                  <Ruler className="w-3 h-3 mr-1" />
                  {truck.availableLinearFeet} ft
                </Badge>
              )}
              {truck.acceptsHazardous && (
                <Badge variant="outline" className="text-yellow-600 border-yellow-600" data-testid="badge-hazmat">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Hazmat OK
                </Badge>
              )}
            </div>
            {truck.dimensions && (
              <p data-testid="text-dimensions">
                <span className="font-medium">Dimensions:</span> {truck.dimensions}
              </p>
            )}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span data-testid="text-available">
                <span className="font-medium">Available:</span> {formatDate(truck.availableFrom)}
                {truck.availableTo && truck.availableTo !== truck.availableFrom && (
                  <> - {formatDate(truck.availableTo)}</>
                )}
              </span>
            </div>
          </div>
        </div>
        {truck.description && (
          <p className="text-base md:text-sm text-muted-foreground" data-testid="text-description">
            {truck.description}
          </p>
        )}
        <Button 
          variant="outline"
          className="w-full" 
          onClick={(e) => { e.stopPropagation(); onViewDetails(truck); }}
          data-testid="button-view-details"
        >
          <Eye className="w-4 h-4 mr-2" />
          View Details
        </Button>
      </div>
    </Card>
  );
}

export default function FindTrucks() {
  const [filters, setFilters] = useState({
    origin: "",
    destination: "",
    minCapacity: "",
    equipmentType: "",
    company: ""
  });
  const [selectedTruck, setSelectedTruck] = useState<TruckWithCarrier | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [radiusEnabled, setRadiusEnabled] = useState(false);
  const [radiusMiles, setRadiusMiles] = useState<50 | 75>(50);
  const [geocodedOrigin, setGeocodedOrigin] = useState<{ lat: number; lng: number; address: string } | null>(null);

  const { data: currentUser } = useQuery<User>({
    queryKey: ['/api/auth/user'],
  });

  const { data: verificationSetting, isLoading: verificationSettingLoading } = useQuery<{ verificationRequired: boolean }>({
    queryKey: ['/api/site-settings/verification-required'],
  });

  const isUserVerified = verificationSettingLoading || verificationSetting?.verificationRequired === false || currentUser?.verificationStatus === "verified";

  useEffect(() => {
    if (!radiusEnabled || !filters.origin.trim()) {
      setGeocodedOrigin(null);
      return;
    }
    let cancelled = false;
    geocodeOriginClient(filters.origin).then(coords => {
      if (!cancelled && coords) {
        setGeocodedOrigin({ ...coords, address: filters.origin.trim() });
      } else if (!cancelled) {
        setGeocodedOrigin(null);
      }
    });
    return () => { cancelled = true; };
  }, [filters.origin, radiusEnabled]);

  const { data: trucks, isLoading } = useQuery<TruckWithCarrier[]>({
    queryKey: ['/api/trucks', { status: 'Available', company: filters.company, radius: radiusEnabled ? radiusMiles : undefined, searchLat: geocodedOrigin?.lat, searchLng: geocodedOrigin?.lng }],
    queryFn: () => {
      const params = new URLSearchParams({ status: 'Available' });
      if (filters.company.trim()) params.set('company', filters.company.trim());
      if (radiusEnabled && geocodedOrigin) {
        params.set('radius', String(radiusMiles));
        params.set('searchLat', String(geocodedOrigin.lat));
        params.set('searchLng', String(geocodedOrigin.lng));
      }
      return fetch(`/api/trucks?${params}`).then(r => r.json());
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const handleViewDetails = (truck: TruckWithCarrier) => {
    setSelectedTruck(truck);
    setModalOpen(true);
  };

  const filteredTrucks = trucks?.filter((truck: TruckWithCarrier) => {
    const originSearch = filters.origin.toLowerCase();
    const destinationSearch = filters.destination.toLowerCase();
    
    const originMatch = radiusEnabled ? true : (
      !originSearch || 
      truck.origin.toLowerCase().includes(originSearch) ||
      (truck.originCity?.toLowerCase().includes(originSearch)) ||
      (truck.originState?.toLowerCase().includes(originSearch)) ||
      (truck.originZip?.includes(filters.origin))
    );
    
    const destinationMatch = !destinationSearch || 
      truck.destination.toLowerCase().includes(destinationSearch) ||
      (truck.destinationCity?.toLowerCase().includes(destinationSearch)) ||
      (truck.destinationState?.toLowerCase().includes(destinationSearch)) ||
      (truck.destinationZip?.includes(filters.destination));

    if (filters.minCapacity && truck.capacity < parseInt(filters.minCapacity)) {
      return false;
    }

    if (filters.equipmentType) {
      if (truck.equipmentType?.toLowerCase() !== filters.equipmentType.toLowerCase()) return false;
    }

    if (filters.company) {
      const companySearch = filters.company.toLowerCase();
      if (!(truck.carrier?.company ?? "").toLowerCase().includes(companySearch)) {
        return false;
      }
    }

    if (originSearch && destinationSearch) {
      if (originMatch && destinationMatch) {
        return true;
      }
      if (truck.acceptsPartialLane && (originMatch || destinationMatch)) {
        return true;
      }
      return false;
    }
    
    if (originSearch && !originMatch) return false;
    if (destinationSearch && !destinationMatch) return false;
    
    return true;
  }) || [];

  const displayTrucks = filteredTrucks.filter(truck => truck.userId !== currentUser?.id);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Find Available Trucks</h1>
      
      <Card className="p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
          <div className="space-y-2">
            <Label htmlFor="filter-origin">Origin</Label>
            <Input 
              id="filter-origin"
              placeholder="City, State, or ZIP"
              value={filters.origin}
              onChange={(e) => setFilters({...filters, origin: e.target.value})}
              data-testid="input-filter-origin"
            />
            <div className="flex items-center gap-2 pt-1">
              <Switch
                id="radius-toggle-trucks"
                checked={radiusEnabled}
                onCheckedChange={setRadiusEnabled}
                data-testid="switch-radius-trucks"
              />
              <Label htmlFor="radius-toggle-trucks" className="text-sm font-normal cursor-pointer">
                Search 50-75 miles radius from origin
              </Label>
            </div>
            {radiusEnabled && (
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setRadiusMiles(50)}
                  className={`flex-1 text-sm py-1 px-3 rounded-md border transition-colors ${radiusMiles === 50 ? 'bg-primary text-primary-foreground border-primary' : 'border-border bg-background hover-elevate'}`}
                  data-testid="button-radius-50-trucks"
                >
                  50 miles
                </button>
                <button
                  type="button"
                  onClick={() => setRadiusMiles(75)}
                  className={`flex-1 text-sm py-1 px-3 rounded-md border transition-colors ${radiusMiles === 75 ? 'bg-primary text-primary-foreground border-primary' : 'border-border bg-background hover-elevate'}`}
                  data-testid="button-radius-75-trucks"
                >
                  75 miles
                </button>
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="filter-destination">Destination</Label>
            <Input 
              id="filter-destination"
              placeholder="City, State, or ZIP"
              value={filters.destination}
              onChange={(e) => setFilters({...filters, destination: e.target.value})}
              data-testid="input-filter-destination"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="filter-capacity">Min Capacity (lbs)</Label>
            <Input 
              id="filter-capacity"
              type="number"
              placeholder="15000"
              value={filters.minCapacity}
              onChange={(e) => setFilters({...filters, minCapacity: e.target.value})}
              data-testid="input-filter-capacity"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="filter-equipment">Equipment Type</Label>
            <Select
              value={filters.equipmentType || "all"}
              onValueChange={(value) => setFilters({...filters, equipmentType: value === "all" ? "" : value})}
            >
              <SelectTrigger id="filter-equipment" data-testid="select-filter-equipment">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {EQUIPMENT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="filter-company">Company Name</Label>
            <Input
              id="filter-company"
              placeholder="e.g., ABC Shipper LLC"
              value={filters.company}
              onChange={(e) => setFilters({...filters, company: e.target.value})}
              data-testid="input-filter-company"
            />
          </div>
        </div>
      </Card>

      <div className="mb-4">
        {radiusEnabled && geocodedOrigin && (
          <p className="text-base md:text-sm text-muted-foreground mb-2" data-testid="text-radius-label-trucks">
            Showing results within {radiusMiles} miles of {geocodedOrigin.address}
          </p>
        )}
        <p className="text-muted-foreground" data-testid="text-count">
          {isLoading ? "Loading..." : `${displayTrucks.length} truck${displayTrucks.length !== 1 ? 's' : ''} available`}
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading trucks...</div>
      ) : displayTrucks.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No trucks found. Try adjusting your filters or post a load to attract carriers.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayTrucks.map(truck => (
            <TruckCard key={truck.id} truck={truck} onViewDetails={handleViewDetails} currentUserVerified={isUserVerified} />
          ))}
        </div>
      )}

      <TruckDetailModal
        truck={selectedTruck}
        open={modalOpen}
        onOpenChange={setModalOpen}
        currentUserId={currentUser?.id}
        currentUserType={currentUser?.userType || undefined}
        currentUserVerified={isUserVerified}
      />
    </div>
  );
}
